import { useLayoutEffect, useRef, useState, type CSSProperties } from "react";
import CoverPreview from "@/components/shared/yearbook-preview/CoverPreview";

interface FittedCoverPreviewProps {
  page: unknown;
  assetsBaseUrl?: string;
  aspectRatio?: number;
  pageWidth?: number;
  pageHeight?: number;
  className?: string;
  style?: CSSProperties;
}

/**
 * CoverPreview peut produire des dimensions différentes selon la taille physique
 * du livre, même lorsque le ratio est identique. Ce cadre mesure son rendu réel
 * et le réduit pour qu'il reste toujours entièrement visible.
 */
export default function FittedCoverPreview({
  page,
  assetsBaseUrl,
  aspectRatio,
  pageWidth,
  pageHeight,
  className,
  style,
}: FittedCoverPreviewProps) {
  const frameRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);

  useLayoutEffect(() => {
    const frame = frameRef.current;
    const content = contentRef.current;
    if (!frame || !content) return;

    const fit = () => {
      const child = content.firstElementChild as HTMLElement | null;
      const contentWidth = Math.max(child?.scrollWidth ?? 0, child?.offsetWidth ?? 0, content.scrollWidth);
      const contentHeight = Math.max(child?.scrollHeight ?? 0, child?.offsetHeight ?? 0, content.scrollHeight);
      if (contentWidth <= 0 || contentHeight <= 0) return;

      setScale(Math.min(1, frame.clientWidth / contentWidth, frame.clientHeight / contentHeight));
    };

    fit();
    const resizeObserver = new ResizeObserver(fit);
    resizeObserver.observe(frame);
    resizeObserver.observe(content);
    const mutationObserver = new MutationObserver(fit);
    mutationObserver.observe(content, { childList: true, subtree: true, attributes: true });

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, [page, aspectRatio]);

  return (
    <div ref={frameRef} className={className} style={style}>
      <div className="relative h-full w-full overflow-hidden">
        <div
          ref={contentRef}
          className="absolute left-1/2 top-1/2"
          style={{ transform: `translate(-50%, -50%) scale(${scale})`, transformOrigin: "center" }}
        >
          <CoverPreview page={page} assetsBaseUrl={assetsBaseUrl} aspectRatio={aspectRatio} pageWidth={pageWidth} pageHeight={pageHeight} />
        </div>
      </div>
    </div>
  );
}